/**
 * Who you are, and the things you can do about it. One panel, both devices.
 *
 * It was written for the phone: the header's identity block — acting-as, the role chip, Sign out —
 * is the first thing to go when the screen is 390px wide, three controls competing with the app's
 * own title for one line. So it moved into the sheet the avatar opens, which is where a phone user
 * goes looking for it because that is where every other app on the device puts it.
 *
 * The note here used to end "Desktop is untouched — the header has room, and moving the controls
 * would be a change for its own sake." That held right up until somebody looked at the two side by
 * side (Tim, 21 August): the desktop was answering the same question in a different shape, which
 * is two designs and two places to change. The header now opens THIS component in a popover — see
 * `ProfileMenu`. Anything added below appears on both, which is the point.
 */

import { useState } from 'react';
import { DISCORD_URL, LEAGUE_URL } from '../domain/config.js';
import { GmBadge } from './GmBadge.js';
import { managerShort, teamOfGm } from '../domain/identity.js';
import { useAvatar } from './avatars.js';
import type { GmId, LeagueStore } from '../store/types.js';
import { PhotoDialog } from './PhotoDialog.js';
import { asFirestore, asMemoryStore } from '../store/provider.js';

export interface IdentityPanelProps {
  /** The manager the app is currently being shown as. */
  gm: GmId;
  /**
   * Your data (Pass 16, call 1): every role's page, so it lives behind every role's door. The
   * figure beside it is the browser store's size, or the league database's last write.
   */
  onYourData?: () => void;
  /** The person actually signed in. Differs from `gm` only while acting as somebody else. */
  actor: GmId;
  roleLabel: string;
  /** Empty when this session cannot act as anybody — the select is not drawn at all. */
  actAs: readonly GmId[];
  actingAs: GmId | null;
  onActAs: (gm: GmId | null) => void;
  onSignOut: () => void;
  store: LeagueStore;
  /** Whose picture this panel edits, and whether it may. */
  canEditAvatar: boolean;
  /**
   * THE DOOR INTO ADMIN, for a commissioner and above. Undefined for everybody else, and a door
   * nobody may open is never drawn.
   *
   * It lives here rather than on Home's commissioner card because that card is on ONE screen:
   * the accepted cost of the 3 September decision was that a commissioner mid-meeting or
   * mid-draft had to go back to Home to reach the settings he was being asked about. Tim, same
   * day: put it under the profile menu. That menu is in the header on every screen and in the
   * More sheet on every phone screen, so the cost is gone.
   */
  onAdmin?: () => void;
  /** Sign-ins waiting on the admin, shown on the row so the header dot has somewhere to land. */
  waiting?: number;
}

export function IdentityPanel(props: IdentityPanelProps) {
  const { gm, actor, roleLabel, actAs, actingAs, onActAs, onSignOut, store } = props;
  const { onAdmin, waiting = 0 } = props;
  const face = useAvatar(gm);
  const [photo, setPhoto] = useState(false);
  const [picking, setPicking] = useState(false);
  const isViewer = roleLabel.toLowerCase() === 'viewer';
  const team = isViewer ? '' : teamOfGm(gm);

  return (
    <div className="idpanel p15">
      {/*
        ACTING AS, AS A BAND INSIDE THE MENU. The global banner from the brief still exists; this
        is the menu's echo of it, and it carries the way back. It names both people because the
        avatar below keeps its own mark while the header wears the borrowed one.
      */}
      {actingAs && (
        <div className="idacting">
          <span>Acting as <b>{managerShort(actingAs)}</b> — saves record your name</span>
          <button type="button" className="linkbtn" onClick={() => onActAs(null)}>Stop</button>
        </div>
      )}

      {/*
        THE IDENTITY BLOCK. Name and role tag on one line, mono caps beside the name; the team on
        its own line beneath, full width, so "Dildo On The Court" and "Shooting 69 This Season" get
        the room they need and ellipsize rather than wrap. A viewer has no team line.
      */}
      <div className="idwho">
        {/*
          THE PICTURE IS ITS OWN DOOR (Tim, 4 September). "Change photo" was a row at the bottom of
          the menu; the thing it changes is at the top. Hovering the face says Edit, and on a phone,
          where nothing hovers, the word stays on. A viewer may set one too (Tim, 4 September): the
          picture belongs to his roster identity, not his seat, so it follows him if his role changes.
        */}
        {props.canEditAvatar ? (
          <button type="button" className="idfacebtn" aria-label={face ? 'Change photo' : 'Add a photo'}
            onClick={() => setPhoto(true)}>
            <GmBadge gm={actingAs ? actor : gm} size="md" className="idface" />
            <span className="idfaceedit" aria-hidden="true">Edit</span>
          </button>
        ) : (
          <GmBadge gm={actingAs ? actor : gm} size="md" className="idface" />
        )}
        <div className="idnames">
          <span className="idline">
            <b>{managerShort(actingAs ? actor : gm)}</b>
            <span className="idrole">{roleLabel}</span>
          </span>
          {team && <span className="idteam">{team}</span>}
        </div>
      </div>

      {/* ADMIN. Rank ≥ 1 only, above the outbound links; the count is access requests waiting. */}
      {onAdmin && (
        <button type="button" className="idrow idpadmin" onClick={onAdmin}>
          <span className="what">Admin</span>
          {waiting > 0 && <span className="idcount">{waiting}</span>}
          <span className="go" aria-hidden="true">›</span>
        </button>
      )}

      {/* ACT AS ANOTHER GM. Admin only. The row opens the list in place rather than a select. */}
      {actAs.length > 0 && (
        <>
          <button type="button" className="idrow" aria-expanded={picking} onClick={() => setPicking(!picking)}>
            <span className="what">Act as another GM</span>
            <span className="go" aria-hidden="true">{picking ? '⌄' : '›'}</span>
          </button>
          {picking && (
            <ul className="idpick">
              {actAs.filter((g) => g !== actor).map((g) => (
                <li key={g}>
                  <button type="button" className={actingAs === g ? 'on' : ''}
                    onClick={() => { onActAs(g); setPicking(false); }}>
                    <GmBadge gm={g} />
                    <span>{managerShort(g)}</span>
                    {!isViewer && <span className="mut">{teamOfGm(g)}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/*
        THE TWO PLACES THIS LEAGUE ALSO HAPPENS, as links out. Yahoo first: it is the one a manager
        reaches for most. Both leave the origin in a tab of their own — a half-finished keeper
        slate is not worth losing to a tap on a menu. EACH WEARS ITS OWN MARK, in the panel's ink;
        neither drags its brand colour into this palette.
      */}
      {props.onYourData && (
        <button type="button" className="idrow iddata" onClick={props.onYourData}>
          <span className="what">Your data</span>
          {yourDataFigure(store) && <span className="idfig">{yourDataFigure(store)}</span>}
          <span className="go" aria-hidden="true">›</span>
        </button>
      )}

      <a className="idrow idpout" href={LEAGUE_URL} target="_blank" rel="noreferrer noopener">
        <YahooMark />
        <span className="what">Yahoo league</span>
        <span className="go" aria-hidden="true">↗</span>
      </a>
      <a className="idrow idpout" href={DISCORD_URL} target="_blank" rel="noreferrer noopener">
        <DiscordMark />
        <span className="what">Discord</span>
        <span className="go" aria-hidden="true">↗</span>
      </a>

      {/*
        ONE WORD FOR IT (Tim, 21 August, twice). "Log out" is his wording; what the press DOES still
        depends on the session, what it says no longer does.
      */}
      <button type="button" className="idrow idout" onClick={onSignOut}>
        <span className="what">Log out</span>
      </button>

      {photo && (
        <PhotoDialog gm={gm} actor={actor} store={store} current={face ?? null} onClose={() => setPhoto(false)} />
      )}
    </div>
  );
}

/**
 * Yahoo's own mark, inlined at 16px on its native 24-unit grid — the same treatment `DiscordMark`
 * below gets, and for the same reasons.
 *
 * THE `y!` GLYPH, not the wordmark. The full "Yahoo!" logotype is a word, and a word set at 16px
 * beside another word ("Open League") reads as a stutter; the glyph is what Yahoo itself puts on a
 * tab and an app icon, so it is what anybody recognises at this size. `currentColor` so it wears
 * the button's ink rather than carrying Yahoo's purple into this palette.
 */
function YahooMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"
      focusable="false" className="idpoutmark">
      <path d="M18.86 1.56L14.27 11.87H19.4L24 1.56H18.86M0 6.71L5.15 18.27L3.3 22.44H7.83L14.69 6.71H10.19L7.39 13.44L4.62 6.71H0M15.62 12.87C13.95 12.87 12.71 14.12 12.71 15.58C12.71 17 13.91 18.19 15.5 18.19C17.18 18.19 18.43 16.96 18.43 15.5C18.43 14.03 17.23 12.87 15.62 12.87Z" />
    </svg>
  );
}

/**
 * Discord's own mark, inlined at 16px on its native 24-unit grid.
 *
 * Not part of `NavIcons`: those are ours — an 18px set on a 1.5 stroke, drawn from what each
 * section IS. This is somebody else's logo, used to name a link to their product, and it is a
 * filled path rather than a stroked one. Mixing it into that set would break both rules the set
 * has. It takes `currentColor` so it wears the button's ink.
 */
function DiscordMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"
      focusable="false" className="idpoutmark">
      <path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
    </svg>
  );
}

/** `412 KB` in the browser, the last write time on the league database, nothing before either. */
function yourDataFigure(store: LeagueStore): string | null {
  const mem = asMemoryStore(store);
  if (mem) {
    const { bytes, available } = mem.persistence;
    return available && bytes > 0 ? `${(bytes / 1024).toFixed(0)} KB` : null;
  }
  const at = asFirestore(store)?.status.savedAt;
  return at ? new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
}
